import { AppModule } from '@estimator/shared';
import {
  LayoutDashboard,
  FolderKanban,
  Building2,
  Layers,
  Hammer,
  Paintbrush,
  Clock,
  TrendingUp,
  Wrench,
  Box,
  Frame,
  PackageOpen,
  Building,
  Gauge,
  DatabaseBackup,
  Cpu,
  Truck,
  Warehouse,
  Calculator,
  Sofa,
  Component,
  Ruler,
} from 'lucide-react';
import { createElement } from 'react';

export interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

const icon = (Icon: any, size = 18) => createElement(Icon, { size });

const SHARED_PROJECTS: NavItem = {
  label: 'Projects',
  icon: icon(FolderKanban),
  path: '/projects',
};

export const MODULE_NAV: Record<AppModule, NavGroup[]> = {
  [AppModule.ESTIMATION]: [
    {
      label: '',
      defaultOpen: true,
      items: [
        { label: 'Dashboard', icon: icon(LayoutDashboard), path: '/estimation' },
        SHARED_PROJECTS,
        { label: 'Companies', icon: icon(Building2), path: '/companies' },
      ],
    },
    {
      label: 'Libraries',
      defaultOpen: false,
      items: [
        { label: 'Materials', icon: icon(Layers), path: '/settings/materials' },
        { label: 'Hardware', icon: icon(Hammer), path: '/settings/hardware' },
        { label: '3D Models', icon: icon(Box), path: '/settings/hardware-model-editor' },
        { label: 'Finishes', icon: icon(Paintbrush), path: '/settings/finishes' },
      ],
    },
    {
      label: 'Templates',
      defaultOpen: false,
      items: [
        { label: 'Products', icon: icon(Box), path: '/settings/products' },
        { label: 'Frames', icon: icon(Frame), path: '/settings/frame-templates' },
        { label: 'Assemblies', icon: icon(PackageOpen), path: '/settings/assembly-templates' },
      ],
    },
    {
      label: 'Configuration',
      defaultOpen: false,
      items: [
        { label: 'Labor Rates', icon: icon(Clock), path: '/settings/labor-rates' },
        { label: 'Margins', icon: icon(TrendingUp), path: '/settings/margins' },
        { label: 'Construction', icon: icon(Wrench), path: '/settings/construction-methods' },
        { label: 'Company', icon: icon(Building), path: '/settings/company' },
        { label: 'Complexity', icon: icon(Gauge), path: '/settings/complexity' },
        { label: 'Upholstery', icon: icon(Sofa), path: '/settings/upholstery' },
        { label: 'Pillow Designs', icon: icon(Sofa), path: '/settings/pillow-designs' },
        { label: 'Pillow Inserts', icon: icon(PackageOpen), path: '/settings/pillow-inserts' },
        { label: 'Oversized Rules', icon: icon(Ruler), path: '/settings/oversized' },
        { label: 'Data Mgmt', icon: icon(DatabaseBackup), path: '/settings/data-management' },
      ],
    },
    {
      label: 'Cria',
      defaultOpen: false,
      items: [
        { label: 'Component templates', icon: icon(Component), path: '/settings/component-templates' },
      ],
    },
  ],

  [AppModule.MANUFACTURING]: [
    {
      label: '',
      defaultOpen: true,
      items: [
        { label: 'Dashboard', icon: icon(LayoutDashboard), path: '/manufacturing' },
        SHARED_PROJECTS,
        { label: 'CNC Queue', icon: icon(Cpu), path: '/cnc-queue' },
      ],
    },
  ],

  [AppModule.LOGISTICS]: [
    {
      label: '',
      defaultOpen: true,
      items: [
        { label: 'Dashboard', icon: icon(Truck), path: '/logistics' },
        SHARED_PROJECTS,
      ],
    },
  ],

  [AppModule.INVENTORY]: [
    {
      label: '',
      defaultOpen: true,
      items: [
        { label: 'Dashboard', icon: icon(Warehouse), path: '/inventory' },
        SHARED_PROJECTS,
      ],
    },
  ],
};

export type TabKey =
  | 'overview'
  | 'knowledge'
  | 'markup'
  | 'areas'
  | 'materials'
  | 'estimate'
  | 'nesting'
  | 'truck-loader'
  | 'viewer3d'
  | 'cnc'
  | 'settings';

export const MODULE_TABS: Record<AppModule, TabKey[]> = {
  [AppModule.ESTIMATION]: [
    'overview',
    'knowledge',
    'markup',
    'areas',
    'materials',
    'estimate',
    'nesting',
    'truck-loader',
    'viewer3d',
    'settings',
  ],
  [AppModule.MANUFACTURING]: ['areas', 'materials', 'nesting', 'cnc'],
  [AppModule.LOGISTICS]: ['truck-loader'],
  [AppModule.INVENTORY]: [],
};

export const MODULE_LABELS: Record<AppModule, string> = {
  [AppModule.ESTIMATION]: 'Estimation',
  [AppModule.MANUFACTURING]: 'Manufacturing',
  [AppModule.LOGISTICS]: 'Logistics',
  [AppModule.INVENTORY]: 'Inventory',
};

export const MODULE_ICONS: Record<AppModule, any> = {
  [AppModule.ESTIMATION]: Calculator,
  [AppModule.MANUFACTURING]: Cpu,
  [AppModule.LOGISTICS]: Truck,
  [AppModule.INVENTORY]: Warehouse,
};
